"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { getFeatureFlags } from "@/lib/feature-flags";
import { useAuth } from "@wtc/ui";
import { useUrlModalControls } from "@wtc/ui";
import { GoogleDriveFilePickerModal } from "../pickers/GoogleDriveFilePickerModal";
import { MarketingAssetLibraryModal } from "@/components/marketing/asset-library/MarketingAssetLibraryModal";
import { useMarketingBrands } from "@/lib/hooks/marketing/useMarketingBrands";
import { Button } from "@/components/ui/button";
import { Images } from "lucide-react";
import type { MaterializedMarketingAsset } from "@/lib/hooks/marketing/useMarketingAssetPicker";
import { RichTextEditorProps } from "./types";
import { useRichTextEditor } from "./hooks/useRichTextEditor";
import { useEditorFormats } from "./hooks/useEditorFormats";
import { useEditorAttachments } from "./hooks/useEditorAttachments";
import { Toolbar } from "./components/Toolbar";
import { EditorContent } from "./components/EditorContent";
import { uploadImage } from "./utils";
import { toast } from "sonner";

export function RichTextEditor({
  value,
  onChange,
  placeholder = "Start typing...",
  className,
  disabled = false,
  email,
  isSecondAccount = false,
  onAttachmentsChange,
  marketingLibraryBrand,
}: RichTextEditorProps) {
  const { getAuthToken } = useAuth();
  const [showMarketingLibrary, setShowMarketingLibrary] = React.useState(false);
  // The prop is free text from a caller, so it is checked against the live
  // brand list before it is used to open a brand's Drive asset library.
  const { isKnownBrand: isRegisteredBrand } = useMarketingBrands();
  const marketingBrand =
    marketingLibraryBrand && isRegisteredBrand(marketingLibraryBrand)
      ? marketingLibraryBrand
      : null;
  const { openModal: openGoogleDriveModal } = useUrlModalControls({
    modalId: "google-drive-picker",
  });
  const featureFlags = getFeatureFlags();

  // Initialize editor
  const editor = useRichTextEditor(value, onChange, placeholder, disabled);

  // Format handlers
  const { setAlignment, toggleFormat, toggleLink } = useEditorFormats(editor);

  // Attachment handlers
  const {
    attachedFiles,
    isUploading,
    handleFileAttachment,
    handleGoogleDriveFileSelect,
    handleAttachmentRemove,
  } = useEditorAttachments(editor, disabled, isSecondAccount, onAttachmentsChange);

  // Image upload handler
  const handleImageUpload = React.useCallback(async () => {
    if (!editor || disabled || isUploading) return;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB");
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      try {
        const token = getAuthToken();
        const imageUrl = await uploadImage(file, token || undefined);
        editor
          .chain()
          .focus()
          .insertContent(`<img src="${imageUrl}" alt="Uploaded image" class="max-w-full h-auto" />`)
          .run();
        toast.success("Image uploaded successfully");
      } catch (error) {
        // Error already handled in uploadImage function
      }
    };

    input.click();
  }, [editor, disabled, isUploading, getAuthToken]);

  // Insert an image picked from the brand's marketing asset library. The picker
  // materializes the Drive file into the public social-media-assets bucket and
  // returns a stable public URL that's safe to embed in an HTML email.
  const handleMarketingAssetSelected = React.useCallback(
    (asset: MaterializedMarketingAsset) => {
      if (!editor || asset.mediaKind !== "image") {
        if (asset.mediaKind !== "image") {
          toast.error("Only images can be inserted into an email body.");
        }
        return;
      }
      editor
        .chain()
        .focus()
        .insertContent(
          `<img src="${asset.publicUrl}" alt="${asset.driveFileName}" class="max-w-full h-auto" />`,
        )
        .run();
    },
    [editor],
  );

  if (!editor) {
    return null;
  }

  return (
    <div className={cn("border rounded-md", className)}>
      {/* Toolbar */}
      <Toolbar
        editor={editor}
        disabled={disabled}
        isUploading={isUploading}
        attachedFilesCount={attachedFiles.size}
        attachedFiles={attachedFiles}
        onToggleFormat={toggleFormat}
        onSetAlignment={setAlignment}
        onToggleLink={toggleLink}
        onImageUpload={handleImageUpload}
        onFileAttachment={handleFileAttachment}
        onAttachmentRemove={handleAttachmentRemove}
        onGoogleDriveClick={openGoogleDriveModal}
        showGoogleDrive={featureFlags.googleDriveAttachments}
      />

      {/* Insert from Marketing Library (brand-scoped Drive assets → public URL) */}
      {marketingBrand && (
        <div className="border-b p-2 bg-muted/20 flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => setShowMarketingLibrary(true)}
            title="Insert an image from the brand marketing library"
          >
            <Images className="h-4 w-4 mr-2" />
            Insert from Marketing Library
          </Button>
        </div>
      )}

      {/* Editor Content */}
      <EditorContent editor={editor} disabled={disabled} />

      {/* Google Drive File Picker Modal */}
      {featureFlags.googleDriveAttachments && (
        <GoogleDriveFilePickerModal />
      )}

      {/* Marketing Asset Library Modal */}
      {marketingBrand && (
        <MarketingAssetLibraryModal
          brand={marketingBrand}
          open={showMarketingLibrary}
          onOpenChange={setShowMarketingLibrary}
          onAssetSelected={handleMarketingAssetSelected}
        />
      )}
    </div>
  );
}
